<script setup>
import debounce from "lodash.debounce";
import { IMaskComponent } from "vue-imask";
import { computed, ref, nextTick } from "vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { getDisplayedSize, getSizeWithSteps } from "../../utils/sizes";
import { NECK_LINE_STEP } from "../../misc/constants";

const emit = defineEmits([
  "update-neck-line-label",
  "update-neck-line-height",
  "reset-neck-line",
  "track-custom-placement"
]);
const renderInput = ref(true);
const props = defineProps({
  label: {
    type: String,
    required: true
  },
  default: {
    type: String,
    required: false,
    default: "0.00"
  },
  value: {
    type: String,
    required: false,
    default: "0.00"
  },
  active: {
    type: Boolean,
    required: true
  },
  position: {
    type: String,
    required: true
  }
});

const isCustom = computed(() => props.active && props.value !== props.default);

const trackCustomPlacement = debounce(() => {
  emit("track-custom-placement", "moved");
}, 500);

const updateNeckLineHeight = height => {
  const roundedHeight = getSizeWithSteps(parseFloat(height));
  emit("update-neck-line-height", { label: props.label, height: roundedHeight });
  trackCustomPlacement();
};

const increaseNeckLineHeight = () => {
  const height = parseFloat(props.value) + NECK_LINE_STEP;
  updateNeckLineHeight(getDisplayedSize(height));
};

const decreaseNeckLineHeight = () => {
  const height = parseFloat(props.value) - NECK_LINE_STEP;
  updateNeckLineHeight(getDisplayedSize(height));
};

// This approach is needed to rerender input after putting the out of range value
const updateValue = async value => {
  renderInput.value = false;
  updateNeckLineHeight(value);
  await nextTick();
  renderInput.value = true;
};
</script>

<template>
  <div
    class="artwork-neck-line"
    role="button"
    :class="{
      'artwork-neck-line--active': props.active,
      'artwork-neck-line--custom': isCustom
    }"
    @click="emit('update-neck-line-label', props.label)"
  >
    <div class="artwork-neck-line__title">{{ props.label }}</div>

    <div class="artwork-neck-line__toggles-wrapper">
      <IMaskComponent
        v-if="renderInput"
        :mask="'num”'"
        :lazy="false"
        :blocks="{ num: { mask: Number, radix: '.' } }"
        class="artwork-neck-line__field"
        :value="`${props.value}”`"
        data-testid="artwork-neck-line-field"
        @keypress.enter.prevent="updateValue($event.target.value)"
        @change="updateValue($event.target.value)"
        @blur="updateValue($event.target.value)"
      />

      <button
        class="artwork-neck-line__toggle artwork-neck-line__toggle--up"
        type="button"
        tabindex="-1"
        aria-label="Increase neck line height"
        role="spinbutton"
        @click="increaseNeckLineHeight"
      />

      <button
        class="artwork-neck-line__toggle artwork-neck-line__toggle--down"
        type="button"
        tabindex="-1"
        aria-label="Decrease neck line height"
        role="spinbutton"
        @click="decreaseNeckLineHeight"
      />
    </div>

    <button
      v-if="isCustom"
      type="button"
      class="artwork-neck-line__reset"
      :class="{
        'artwork-neck-line__reset--first': position === 'left',
        'artwork-neck-line__reset--last': position === 'right'
      }"
      @click="emit('reset-neck-line', { label: props.label, height: props.default })"
    >
      <UseSvg icon="refresh" type="print" class="artwork-neck-line__reset-icon" />
      Reset to Default
    </button>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/pod/settings";
@import "frontend/init/jiffy/mixin";
@import "frontend/init/jiffy/button/mixin";

.artwork-neck-line {
  $this: &;

  border: 2px solid transparent;
  border-radius: 7px;
  padding: 7px;
  position: relative;

  &--active {
    border-color: $pod-premium-bg;
    background-color: $white;

    #{$this}__field {
      border-color: $pod-gray-modern-900;
    }

    #{$this}__toggle {
      display: flex;
    }
  }

  &--custom {
    margin-bottom: 40px;
  }

  &__title {
    font-size: 14px;
    font-weight: bold;
    text-align: center;
    margin-bottom: 4px;
    color: $pod-gray-modern-900;
  }

  &__field {
    display: block;
    width: 100%;
    height: 40px;
    color: $pod-gray-modern-900;
    border: 2px solid $pod-gray-modern-200;
    border-radius: 4px;
    text-align: center;
    font-weight: 600;
    font-size: 14px;
  }

  &__toggles-wrapper {
    position: relative;
  }

  &__toggle {
    @include clear-button;

    width: 22px;
    height: 50%;
    position: absolute;
    right: 0;
    display: none;
    align-items: center;
    justify-content: center;

    &::before {
      content: "";
      display: block;
      width: 0;
      height: 0;
      border-style: solid;
    }

    &::after {
      content: "";
      position: absolute;
      width: 50px;
      height: 50px;
      left: 0;

      @include with-tablet {
        display: none;
      }
    }

    &--up {
      top: 0;
      padding-top: 12px;

      &::before {
        border-width: 0 4px 6px 4px;
        border-color: transparent transparent $pod-gray-modern-900 transparent;
      }

      &::after {
        bottom: 0;
      }
    }

    &--down {
      bottom: 0;
      padding-bottom: 12px;

      &::before {
        border-width: 6px 4px 0 4px;
        border-color: $pod-gray-modern-900 transparent transparent transparent;
      }

      &::after {
        top: 0;
      }
    }
  }

  &__reset {
    @include clear-button;

    display: flex;
    align-items: center;
    width: 140px;
    height: 32px;
    position: absolute;
    top: 100%;
    left: 50%;
    margin: 10px 0 0 -62px;
    font-size: 14px;
    font-weight: 500;
    color: $pod-gray-modern-900;

    &--first {
      left: 0;
      margin-left: 0;
    }

    &--last {
      left: auto;
      margin-left: 0;
      right: 0;
    }
  }

  &__reset-icon {
    margin-right: 8px;
    width: 19px;
    height: 18px;
  }
}
</style>
